import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';

import { TooltipDirective } from '../tooltip/tooltip.directive';
import type { OpenShiftCardData } from '../open-shift-card/open-shift-card';

/**
 * OpenShiftListRow Component
 *
 * Displays an open shift unit in a horizontal row format for list views.
 * Supports application, swap, and takeover types.
 */
@Component({
  selector: 'zag-open-shift-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ZagIcon, TranslatePipe, TooltipDirective],
  host: {
    '[class.zag-open-shift-list-row]': 'true',
    '[class.zag-open-shift-list-row--interactive]': 'isInteractive()',
    '(click)': 'onRowClick()',
  },
  template: `
    <div class="zag-open-shift-list-row__color-bar" [style.background-color]="shift().shiftColor"></div>
    <div class="zag-open-shift-list-row__info">
      <span class="zag-open-shift-list-row__name">{{ shift().shiftName }}</span>
      <span class="zag-open-shift-list-row__time">{{ shift().startTime }} – {{ shift().endTime }}</span>
    </div>
    <span class="zag-open-shift-list-row__date">{{ shift().dateLabel }}</span>
    <span class="zag-open-shift-list-row__team">{{ shift().teamName }}</span>
    @if (isSwapType()) {
      <span class="zag-open-shift-list-row__employee">
        <span zagIcon="person" class="zag-open-shift-list-row__employee-icon"></span>
        {{ shift().assignedEmployeeName }}
      </span>
      <span
        class="zag-open-shift-list-row__type-badge"
        [class.zag-open-shift-list-row__type-badge--swap]="shift().type === 'swap'"
        [class.zag-open-shift-list-row__type-badge--takeover]="shift().type === 'takeover'"
      >
        @if (shift().type === 'swap') {
          <span zagIcon="swap_horiz"></span>
          {{ 'shiftSwaps.typeLabelSwap' | translate }}
        } @else {
          <span zagIcon="person_add"></span>
          {{ 'shiftSwaps.typeLabelTakeover' | translate }}
        }
      </span>
    } @else {
      <span class="zag-open-shift-list-row__slots">
        {{ 'shiftApplications.availableSlots' | translate: { count: shift().availableSlots } }}
      </span>
    }
    <div class="zag-open-shift-list-row__action">
      @if (isSwapType()) {
        @if (shift().hasPendingRequest) {
          <!-- Pending: cancel icon + pending badge -->
          <span class="zag-open-shift-list-row__badge zag-open-shift-list-row__badge--pending">
            <span zagIcon="hourglass_top"></span>
            {{ 'shiftSwaps.pendingBadge' | translate }}
          </span>
          <button
            type="button"
            class="zag-open-shift-list-row__cancel-icon"
            [attr.aria-label]="'shiftSwaps.cancelRequest' | translate"
            (click)="onCancel($event)"
          >
            <span zagIcon="close"></span>
          </button>
        } @else if (!shift().eligible) {
          <span
            class="zag-open-shift-list-row__badge zag-open-shift-list-row__badge--ineligible"
            [zagTooltip]="ineligibilityTooltip()"
            tooltipPosition="below"
          >
            <span zagIcon="block"></span>
            {{ 'shiftApplications.notEligible' | translate }}
          </span>
        } @else {
          <!-- Eligible swap/takeover: row click fires action, show chevron hint -->
          <span class="zag-open-shift-list-row__cta-hint">
            @if (applying()) {
              <span zagIcon="sync" class="zag-open-shift-list-row__spinner"></span>
            } @else {
              <span zagIcon="chevron_right"></span>
            }
          </span>
        }
      } @else {
        @if (shift().alreadyApplied) {
          <span class="zag-open-shift-list-row__badge zag-open-shift-list-row__badge--applied">
            <span zagIcon="how_to_reg"></span>
            {{ 'shiftApplications.alreadyAppliedBadge' | translate }}
          </span>
        } @else if (shift().alreadyAssigned) {
          <span class="zag-open-shift-list-row__badge zag-open-shift-list-row__badge--assigned">
            <span zagIcon="check_circle"></span>
            {{ 'shiftApplications.alreadyAssigned' | translate }}
          </span>
        } @else if (!shift().eligible) {
          <span
            class="zag-open-shift-list-row__badge zag-open-shift-list-row__badge--ineligible"
            [zagTooltip]="ineligibilityTooltip()"
            tooltipPosition="below"
          >
            <span zagIcon="block"></span>
            {{ 'shiftApplications.notEligible' | translate }}
          </span>
        } @else {
          <!-- Eligible application: row click fires action, show chevron hint -->
          <span class="zag-open-shift-list-row__cta-hint">
            @if (applying()) {
              <span zagIcon="sync" class="zag-open-shift-list-row__spinner"></span>
            } @else {
              <span zagIcon="chevron_right"></span>
            }
          </span>
        }
      }
    </div>
  `,
  styles: [
    `
      .zag-open-shift-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        transition: background-color 0.15s;
      }

      .zag-open-shift-list-row--interactive {
        cursor: pointer;

        &:hover {
          background-color: var(--surface-color-hover, var(--surface-color-secondary));
        }
      }

      .zag-open-shift-list-row__color-bar {
        width: 4px;
        height: 32px;
        border-radius: 2px;
        flex-shrink: 0;
      }

      .zag-open-shift-list-row__info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 120px;
      }

      .zag-open-shift-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-open-shift-list-row__time {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        font-family: var(--font-family-mono, monospace);
      }

      .zag-open-shift-list-row__date {
        flex: 1;
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
      }

      .zag-open-shift-list-row__team {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        min-width: 80px;
      }

      .zag-open-shift-list-row__slots {
        font-size: var(--font-size-sm);
        color: var(--color-primary);
        font-weight: 500;
        white-space: nowrap;
        min-width: 100px;
      }

      .zag-open-shift-list-row__employee {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        min-width: 100px;
      }

      .zag-open-shift-list-row__employee-icon {
        font-size: 14px;
        color: var(--text-color-tertiary);
      }

      .zag-open-shift-list-row__type-badge {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-xs);
        font-weight: 500;
        padding: 2px var(--spacing-xs);
        border-radius: var(--border-radius-sm);
        white-space: nowrap;
      }

      .zag-open-shift-list-row__type-badge--swap {
        color: var(--color-primary);
        background: color-mix(in srgb, var(--color-primary) 10%, transparent);
      }

      .zag-open-shift-list-row__type-badge--takeover {
        color: var(--color-success);
        background: color-mix(in srgb, var(--color-success) 10%, transparent);
      }

      .zag-open-shift-list-row__action {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        margin-left: auto;
      }

      /* Cancel icon — mirrors open-shift-card cancel icon */
      .zag-open-shift-list-row__cancel-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: none;
        background: none;
        cursor: pointer;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-tertiary);
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover {
          background: color-mix(in srgb, var(--color-error) 10%, transparent);
          color: var(--color-error);
        }
      }

      /* Subtle chevron hint for eligible rows */
      .zag-open-shift-list-row__cta-hint {
        color: var(--text-color-tertiary);
        display: flex;
        align-items: center;
      }

      .zag-open-shift-list-row__badge {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-xs);
        font-size: var(--font-size-sm);
        font-weight: 500;
      }

      .zag-open-shift-list-row__badge--applied {
        color: var(--color-success);
      }

      .zag-open-shift-list-row__badge--assigned {
        color: var(--color-success);
      }

      .zag-open-shift-list-row__badge--ineligible {
        color: var(--text-color-secondary);
      }

      .zag-open-shift-list-row__badge--pending {
        color: var(--color-warning);
      }

      .zag-open-shift-list-row__spinner {
        animation: zag-shift-row-spin 1s linear infinite;
      }

      @keyframes zag-shift-row-spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class OpenShiftListRow {
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  /** The open shift data to display */
  readonly shift = input.required<OpenShiftCardData>();

  /** Whether the apply action is in progress */
  readonly applying = input(false);

  /** Emitted when the user clicks the Apply button (application type) */
  readonly applyClick = output<OpenShiftCardData>();

  /** Emitted when the user clicks the Request Swap button (swap type) */
  readonly swapClick = output<OpenShiftCardData>();

  /** Emitted when the user clicks the Request Takeover button (takeover type) */
  readonly takeoverClick = output<OpenShiftCardData>();

  /** Emitted when the user clicks Cancel on a pending request */
  readonly cancelClick = output<OpenShiftCardData>();

  /** Whether this is a swap or takeover card */
  protected readonly isSwapType = computed(() => {
    const type = this.shift().type;
    return type === 'swap' || type === 'takeover';
  });

  /** Whether the row should respond to click (not already applied/assigned/ineligible) */
  protected readonly isInteractive = computed(() => {
    const s = this.shift();
    return s.eligible && !s.alreadyApplied && !s.alreadyAssigned && !s.hasPendingRequest;
  });

  /** Row click fires the primary action */
  onRowClick(): void {
    if (!this.isInteractive()) return;
    const s = this.shift();
    if (s.type === 'swap') {
      this.swapClick.emit(s);
    } else if (s.type === 'takeover') {
      this.takeoverClick.emit(s);
    } else {
      this.applyClick.emit(s);
    }
  }

  /** Tooltip text for the "Not Eligible" badge, joining all reason translations */
  protected readonly ineligibilityTooltip = computed(() => {
    this.currentLang();
    const reasons = this.shift().ineligibilityReasons;
    if (reasons.length === 0) return '';
    return reasons.map(r => this.translate.instant(`shiftApplications.reasons.${r}`)).join('\n');
  });

  onApply(event: Event): void {
    event.stopPropagation();
    this.applyClick.emit(this.shift());
  }

  onSwap(event: Event): void {
    event.stopPropagation();
    this.swapClick.emit(this.shift());
  }

  onTakeover(event: Event): void {
    event.stopPropagation();
    this.takeoverClick.emit(this.shift());
  }

  onCancel(event: Event): void {
    event.stopPropagation();
    this.cancelClick.emit(this.shift());
  }
}
